import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Fade,
  Avatar,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SpeedIcon from '@mui/icons-material/Speed';
import ExtensionIcon from '@mui/icons-material/Extension';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import DoneAllIcon from '@mui/icons-material/DoneAll';

const DianExtractorView = () => {
  const [syncedDocs, setSyncedDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [extensionStatus, setExtensionStatus] = useState({ active: false, version: '0.0' });
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  
  const [syncMonth, setSyncMonth] = useState(new Date().getMonth() + 1);
  const [syncYear, setSyncYear] = useState(new Date().getFullYear());
  const [syncStartDate, setSyncStartDate] = useState('');
  const [syncEndDate, setSyncEndDate] = useState('');
  
  const [autoSubmit, setAutoSubmit] = useState(true);
  const [downloadLocal, setDownloadLocal] = useState(false);
  const [renameFiles, setRenameFiles] = useState(false);
  const [indications, setIndications] = useState('');
  
  const theme = useTheme();

  useEffect(() => {
    const firstDay = `${syncYear}-${String(syncMonth).padStart(2, '0')}-01`;
    const lastDayNum = new Date(syncYear, syncMonth, 0).getDate();
    const lastDay = `${syncYear}-${String(syncMonth).padStart(2, '0')}-${String(lastDayNum).padStart(2, '0')}`;
    setSyncStartDate(firstDay);
    setSyncEndDate(lastDay);
  }, [syncMonth, syncYear]);

  useEffect(() => {
    const checkExtension = () => {
      const active = document.body.getAttribute('data-dian-helper-active') === 'true';
      const version = document.body.getAttribute('data-dian-helper-version') || '';
      // Cada copia de la extensión (v1.9.6+) se registra como "<id>@<versión>".
      // Si hay más de una, o la versión visible no es de una copia registrada, hay copias viejas compitiendo.
      const instances = (document.body.getAttribute('data-dian-helper-instances') || '').split(',').filter(Boolean);
      const duplicated = instances.length > 1 || (instances.length > 0 && !instances.some((i) => i.endsWith(`@${version}`)));
      setExtensionStatus({ active, version, duplicated });
    };

    checkExtension();
    const timer = setInterval(checkExtension, 1200);

    const handleResponse = (e) => {
      if (e.detail?.success === false) {
        setIsSyncing(false);
        if (e.detail.error === 'TAB_NOT_FOUND') {
          setError({ 
            severity: 'warning', 
            message: 'No se detectó el portal de la DIAN activa.',
            instruction: 'Por favor, abre la pestaña de la DIAN y pulsa de nuevo.'
          });
        } else {
          setError({ severity: 'error', message: e.detail.message || 'Error en la extensión.' });
        }
      } else if (e.detail?.success) {
        setIsSyncing(true);
        setError(null);
      }
    };

    const handleError = (e) => {
      setIsSyncing(false);
      setError({ 
        severity: 'error', 
        message: e.detail.message, 
        instruction: e.detail.code === 'RELOAD_REQUIRED' ? 'Recarga esta pestaña para reconectar.' : null
      });
    };

    const handleStatusUpdate = (e) => {
      const doc = e.detail;
      if (doc.isComplete) {
        setIsSyncing(false);
        if (doc.error) {
          setError({ severity: 'warning', message: doc.error });
          return;
        }
        setSnackbar({ open: true, message: '✅ Sincronización completada.' });
        return;
      }
      setSyncedDocs(prev => {
        const exists = prev.some(d => d.trackId === doc.trackId);
        if (!exists) return [doc, ...prev];
        return prev.map((previousDoc) => (previousDoc.trackId === doc.trackId && previousDoc.success === false) ? doc : previousDoc);
      });
    };

    window.addEventListener('DianHelperResponse', handleResponse);
    window.addEventListener('DianHelperError', handleError);
    window.addEventListener('DianHelperStatusUpdate', handleStatusUpdate);

    return () => {
      clearInterval(timer);
      window.removeEventListener('DianHelperResponse', handleResponse);
      window.removeEventListener('DianHelperError', handleError);
      window.removeEventListener('DianHelperStatusUpdate', handleStatusUpdate);
    };
  }, []);

  const handleSyncMonthly = () => {
    if (!extensionStatus.active) {
      setError('Debes tener la extensión DIAN Helper instalada y activa.');
      return;
    }

    if (!autoSubmit && !downloadLocal) {
        setError({
            severity: 'warning',
            message: 'No hay acción seleccionada.',
            instruction: 'Activa "Enviar al servidor" o "Descargar localmente" para iniciar.'
        });
        return;
    }

    const storedUser = localStorage.getItem('invoice_user');
    let userUUID = '';
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        userUUID = user.id || '';
      } catch (e) {}
    }

    const event = new CustomEvent('DianHelperSyncMonthV17', {
      detail: {
        startDate: syncStartDate.replace(/-/g, '/'),
        endDate: syncEndDate.replace(/-/g, '/'),
        autoSubmit,
        downloadLocal,
        renameFiles,
        webhookUrl: 'https://panel.asistentesautonomos.com/webhook/cadb9fa9-6438-48a6-b98d-87827be320aa',
        user_uuid: userUUID,
        indications
      }
    });

    window.dispatchEvent(event);
    setSyncedDocs([]); // Clear list for new run
    setIsSyncing(true);
    setSnackbar({ open: true, message: '🚀 Sincronización iniciada...' });
  };

  const handleCancelSync = () => {
    window.dispatchEvent(new CustomEvent('DianHelperCancelSync'));
    setIsSyncing(false);
    setSnackbar({ open: true, message: '⏹ Sincronización cancelada por el usuario.' });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
          <SpeedIcon fontSize="large" />
          DIAN Massive Extractor{extensionStatus.active && extensionStatus.version ? ` v${extensionStatus.version}` : ''}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button 
            variant="outlined" 
            size="small" 
            startIcon={<HelpOutlineIcon />} 
            onClick={() => setShowInstallGuide(true)}
            sx={{ borderRadius: 20 }}
          >
            ¿Cómo instalar?
          </Button>
          <Chip 
            icon={extensionStatus.active ? <CheckCircleOutlineIcon /> : <ExtensionIcon />} 
            label={!extensionStatus.active ? "Extensión Inactiva" : extensionStatus.duplicated ? "Varias copias de la extensión instaladas" : `Conectado`}
            color={extensionStatus.active && !extensionStatus.duplicated ? "success" : "warning"}
            variant="filled"
          />
        </Box>
      </Box>
      
      {error && (
        <Fade in={!!error}>
          <Alert 
            severity={error.severity || 'error'} 
            sx={{ mb: 3, borderLeft: `5px solid ${error.severity === 'warning' ? '#ed6c02' : '#d32f2f'}` }} 
            onClose={() => setError(null)}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{error.message}</Typography>
            {error.instruction && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>{error.instruction}</Typography>
            )}
          </Alert>
        </Fade>
      )}

      {/* Panel de Sincronización */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.01), border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DateRangeIcon color="primary" /> Configuración de Extracción
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Mes</InputLabel>
              <Select value={syncMonth} label="Mes" onChange={(e) => setSyncMonth(e.target.value)}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                  <MenuItem key={m} value={m}>
                    {new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(new Date(2022, m - 1))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Año</InputLabel>
              <Select value={syncYear} label="Año" onChange={(e) => setSyncYear(e.target.value)}>
                {[2023, 2024, 2025, 2026].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" label="Desde" type="date" value={syncStartDate} onChange={(e) => setSyncStartDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField size="small" label="Hasta" type="date" value={syncEndDate} onChange={(e) => setSyncEndDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            {isSyncing ? (
              <Button fullWidth variant="contained" color="error" startIcon={<PauseCircleIcon />} onClick={handleCancelSync} sx={{ py: 1 }}>
                Cancelar Sincronización
              </Button>
            ) : (
              <Button fullWidth variant="contained" startIcon={<RefreshIcon />} onClick={handleSyncMonthly} disabled={!extensionStatus.active} sx={{ py: 1, fontWeight: 'bold' }}>
                Iniciar Extracción
              </Button>
            )}
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 1 }}>
              <FormControlLabel
                control={<Checkbox checked={autoSubmit} onChange={(e) => setAutoSubmit(e.target.checked)} color="primary" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: '600' }}>Enviar automáticamente al servidor</Typography>
                    <Typography variant="caption" color="text.secondary">Los archivos se cargarán directo a tu contabilidad.</Typography>
                  </Box>
                }
              />
              
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={downloadLocal} onChange={(e) => setDownloadLocal(e.target.checked)} color="primary" />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: '600' }}>Descargar copia local (ZIP)</Typography>
                      <Typography variant="caption" color="text.secondary">Guarda los archivos en tu carpeta de descargas.</Typography>
                    </Box>
                  }
                />
                <Fade in={downloadLocal}>
                   <Box sx={{ ml: 4 }}>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={renameFiles} onChange={(e) => setRenameFiles(e.target.checked)} color="primary" />}
                        label={<Typography variant="caption">Renombrar automáticamente (Emisor_Fecha)</Typography>}
                    />
                   </Box>
                </Fade>
              </Box>

              <Box sx={{ flexGrow: 1 }}>
                <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 0, p: 0 }}>
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', cursor: 'pointer' }}>
                        <SettingsIcon sx={{ fontSize: 14 }} /> Opciones avanzadas
                    </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1, pt: 0 }}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        label="Nota de envío / Prefix"
                        value={indications}
                        onChange={(e) => setIndications(e.target.value)}
                        placeholder="Ej: Facturas Noviembre 2025"
                    />
                    </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista de Procesamiento en Tiempo Real */}
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleOutlineIcon color="success" /> 
        {isSyncing ? 'Procesando Documentos...' : 'Historial de esta sesión'}
        {isSyncing && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </Typography>

      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, minHeight: 300 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Emisor</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Folio / Prefijo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ID DIAN</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acción</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Sincronización</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {syncedDocs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                   <Typography color="text.secondary">
                     {isSyncing ? "Esperando la primera factura..." : "No hay documentos procesados en esta sesión."}
                   </Typography>
                   {!isSyncing && !extensionStatus.active && (
                     <Alert severity="info" size="small" sx={{ mt: 2, display: 'inline-flex' }}>
                        Asegúrate de tener la extensión instalada para ver el progreso aquí.
                     </Alert>
                   )}
                </TableCell>
              </TableRow>
            ) : (
              syncedDocs.map((doc) => (
                <TableRow key={doc.trackId} hover>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {doc.emisor}
                  </TableCell>
                  <TableCell>{doc.fecha}</TableCell>
                  <TableCell>{doc.folio || '---'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', opacity: 0.7 }}>
                     {doc.trackId.substring(0, 8)}...
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                       {autoSubmit && (
                        <Tooltip title="Subido al servidor">
                            <Chip size="small" color="primary" icon={<CheckCircleOutlineIcon />} label="Enviado" variant="outlined" />
                        </Tooltip>
                       )}
                       {downloadLocal && (
                        <Tooltip title="Descargado en PC">
                            <Chip size="small" color="success" icon={<FileDownloadIcon />} label="Local" variant="outlined" />
                        </Tooltip>
                       )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {doc.success === true && (
                        <Tooltip title="Sincronizado correctamente con la nube">
                          <Chip
                            size="small"
                            color="success"
                            icon={<CheckCircleOutlineIcon />}
                            label="Sincronizado"
                          />
                        </Tooltip>
                      )}

                      {doc.success === false && (
                        <Tooltip title={doc.error || "Error crítico en la sincronización"}>
                          <Chip
                            size="small"
                            color="error"
                            icon={<ErrorOutlineIcon />}
                            label="Fallo"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="text" sx={{ color: 'text.secondary' }} onClick={() => setSyncedDocs([])} disabled={syncedDocs.length === 0} startIcon={<DeleteIcon />}>
          Limpiar historial de sesión
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Guía de Instalación */}
      <Dialog 
        open={showInstallGuide} 
        onClose={() => setShowInstallGuide(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RocketLaunchIcon color="primary" /> Guía de Instalación: DIAN Helper
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            Sigue estos 5 pasos para activar la sincronización masiva:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, my: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '1rem' }}>1</Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Descarga el paquete</Typography>
                <Typography variant="body2" color="text.secondary">Descarga el archivo ZIP de la extensión a tu computador.</Typography>
                <Button 
                  href="/dian-helper.zip" 
                  download 
                  variant="contained" 
                  size="small" 
                  startIcon={<FileDownloadIcon />}
                  sx={{ mt: 1, borderRadius: 2 }}
                >
                  Descargar Extensión (.ZIP)
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32, fontSize: '1rem' }}>2</Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Descomprime el archivo</Typography>
                <Typography variant="body2" color="text.secondary">Busca el archivo descargado y extrae la carpeta en un lugar seguro (ej: Mis Documentos).</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32, fontSize: '1rem' }}>3</Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Abre Extensiones en Chrome/Edge</Typography>
                <Typography variant="body2" color="text.secondary">
                  Copia y pega este enlace en tu navegador: <code style={{ background: '#eee', padding: '2px 4px' }}>chrome://extensions/</code>
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32, fontSize: '1rem' }}>4</Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Activa el Modo Desarrollador</Typography>
                <Typography variant="body2" color="text.secondary">
                  En la esquina superior derecha, activa el interruptor que dice <b>"Modo desarrollador"</b>.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32, fontSize: '1rem' }}>5</Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Cargar descomprimida</Typography>
                <Typography variant="body2" color="text.secondary">
                  Pulsa el botón <b>"Cargar descomprimida"</b> y selecciona la carpeta que extrajiste en el Paso 2. 
                </Typography>
                <Box sx={{ mt: 1, p: 1, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1, border: `1px dashed ${theme.palette.success.main}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DoneAllIcon color="success" fontSize="small" />
                  <Typography variant="caption" color="success.dark" sx={{ fontWeight: 'bold' }}>¡Listo! El icono del cohete azul debería aparecer.</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            Una vez instalada, regresa aquí y refresca la página (F5) para empezar a sincronizar.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstallGuide(false)} sx={{ fontWeight: 'bold' }}>Cerrar Guía</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DianExtractorView;
